import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAllQuestions,
  addCampaignLead,
  addVisitor,
  updateCampaign
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import Loader from '../../../shared/components/ui/Loader';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import LOGO from '../../../shared/assets/svg/LogoWhite.svg';
import { Phone, Mail } from 'lucide-react';
import Network from '../../../shared/assets/svg/mobNetwrok.svg';

const TemplateOne = () => {
  const userId = Cookies.get('userId');
  const { campaign_id, project_id, preview } = useParams();
  const [isloading, setisLoading] = useState(false);
  const isPreviewMode = preview === 'true' || false;
  const [isSubmitting, setIsSubmitting] = useState(isPreviewMode);
  const navigate = useNavigate();
  const [userCity, setUserCity] = useState('');
  const [showMobileView, setShowMobileView] = useState(false);
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [marketingTemplate, setMarketingTemplate] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedColor, setSelectedColor] = useState('white');

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setShowMobileView(window?.innerWidth < 768);
    };
    checkMobile();
    window?.addEventListener('resize', checkMobile);
    return () => window?.removeEventListener('resize', checkMobile);
  }, []);

  // Track page view
  useEffect(() => {
    mixpanel.track('Template One Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        console.log('Location', data);
        setUserCity(data.city || '');
      })
      .catch((err) => console.error('IP info fetch failed:', err));
  }, []);

  const {
    data: AllQuestions = [],
    refetch: fetchCampaigns,
    isLoading: isQuestionsLoading
  } = useQuery({
    queryKey: ['getAllQuestions', campaign_id],
    queryFn: () => getAllQuestions(campaign_id),
    enabled: !!campaign_id
  });

  // Extract marketing template and company info from questions response
  useEffect(() => {
    if (AllQuestions?.length > 0) {
      const firstQuestion = AllQuestions[0];
      if (firstQuestion?.campaign?.project?.marketingTemplate) {
        setMarketingTemplate(firstQuestion.campaign.project.marketingTemplate);
        // Set color from template, default to 'white' if not found
        setSelectedColor(firstQuestion?.campaign?.project?.marketingTemplate?.color || 'white');
      }
      if (firstQuestion?.campaign?.company) {
        setCompanyInfo(firstQuestion.campaign.company);
      }
    }
  }, [AllQuestions]);

  useEffect(() => {
    setisLoading(isQuestionsLoading);
  }, [isQuestionsLoading]);

  useEffect(() => {
    if (campaign_id) {
      fetchCampaigns();
    }
  }, [campaign_id]);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (AllQuestions?.length > 0) {
      const initialData = {};
      AllQuestions.forEach((q) => {
        initialData[q.name] = '';
      });
      setFormData(initialData);
    }
  }, [AllQuestions]);

  const handleChange = (e, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: e?.target?.value
    }));
  };

  useEffect(() => {
    const hasVisited = document?.cookie?.includes('visited=true');
    if (!hasVisited && userCity) {
      const payload = { location: userCity, campaign_id };
      addVisitor(payload);
      document.cookie = 'visited=true; max-age=86400';
    }
  }, [userCity]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const payload = { ...formData, campaign_id, location: userCity };
    console.log('Form Payload:', payload);
    handleAddLead(payload);
  };

  const { mutate: handleAddLead, isLoading: isAddingLead } = useMutation({
    mutationFn: (payload) => addCampaignLead(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (res) => {
      toast.success('Thank you! Your details have been submitted.');
      setIsSubmitting(false);
      const resetData = {};
      AllQuestions.forEach((q) => {
        resetData[q.name] = '';
      });
      setFormData(resetData);
    },
    onError: (err) => {
      console.error('Error creating new lead:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating lead');
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    setisLoading(isAddingLead);
  }, [isAddingLead]);

  const formatLabel = (name) => {
    return name
      ?.split('_')
      ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      ?.join(' ');
  };

  const isNumericKey = (e) => {
    const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowed?.includes(e?.key)) return;
    if (!/^[0-9]$/.test(e?.key)) {
      e?.preventDefault();
    }
  };

  const renderInput = (name, value, isMobile = false) => {
    const baseClasses = isMobile
      ? `w-full p-2 rounded text-xs bg-white/20 backdrop-blur-sm ${
          selectedColor === 'white'
            ? 'text-white placeholder-white/70'
            : 'text-black placeholder-black/70'
        }`
      : `w-full p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 bg-white/20 backdrop-blur-sm ${
          selectedColor === 'white'
            ? 'text-white placeholder-white/70'
            : 'text-black placeholder-black/70'
        }`;

    const commonProps = {
      name,
      value: value || '',
      onChange: (e) => handleChange(e, name),
      placeholder: 'Enter here',
      required: true,
      disabled: isPreviewMode,
      className: baseClasses
    };

    if (name === 'address') {
      return <textarea {...commonProps} rows={isMobile ? 2 : 3} />;
    }

    if (name === 'email') {
      return <input type="email" {...commonProps} />;
    }

    if (['phone_number', 'alternate_phone_number']?.includes(name)) {
      return (
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{10}"
          minLength={10}
          maxLength={10}
          disabled={isPreviewMode}
          onKeyDown={isNumericKey}
          {...commonProps}
        />
      );
    }

    if (name === 'budget') {
      return <input type="text" inputMode="numeric" onKeyDown={isNumericKey} {...commonProps} />;
    }

    return <input type="text" {...commonProps} />;
  };

  const onConfirm = () => {
    const currentUrl = window?.location?.href;
    const urlParts = currentUrl?.split('/');
    if (urlParts?.[urlParts?.length - 1] === 'true') {
      urlParts[urlParts?.length - 1] = 'false';
    }
    const modifiedUrl = urlParts?.join('/');
    const payload = {
      url: modifiedUrl,
      template: ''
    };
    addCampaign(payload);
  };

  const { mutate: addCampaign } = useMutation({
    mutationFn: (payload) => {
      updateCampaign(campaign_id, payload);
    },
    onMutate: () => {
      setIsSubmitting(true);
      setisLoading(true);
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      setisLoading(false);
      toast.success('Template Added To Campaign');
      navigate('/builder/marketing/forms');
    },
    onError: (err) => {
      setIsSubmitting(false);
      setisLoading(false);
      console.error('Error adding Template to campaign:', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error adding template to campaign'
      );
    }
  });

  const handleMobileInterest = () => {
    setShowMobileForm(true);
  };

  const getBackgroundImage = () => {
    if (showMobileView && marketingTemplate?.phoneBanner_url) {
      return marketingTemplate.phoneBanner_url;
    }
    if (!showMobileView && marketingTemplate?.webBanner_url) {
      return marketingTemplate.webBanner_url;
    }
    return 'linear-gradient(to bottom right, #808080, #BEBEBE)';
  };

  return (
    <>
      <Loader isVisible={isloading} />

      {!showMobileView ? (
        // Desktop View
        <div
          className="min-h-screen relative flex items-center flex-col overflow-auto"
          style={{
            backgroundImage: marketingTemplate?.webBanner_url
              ? `url(${marketingTemplate.webBanner_url})`
              : 'linear-gradient(to bottom right, #808080, #BEBEBE)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
          {/* Header with Logo */}
          <div
            className={`p-4 flex items-center justify-end w-full ${
              selectedColor === 'white' ? 'text-white' : 'text-black'
            }`}>
            <img src={LOGO} alt="HousinGram Logo" className="h-8" />
          </div>

          {/* Content */}
          <div className="relative z-10 pl-8 flex w-full flex-col">
            {/* Left Content */}
            <div className="flex-1 w-[65%]">
              <h2
                className={`text-4xl font-semibold mb-4 leading-tight ${
                  selectedColor === 'white' ? 'text-white' : 'text-black'
                }`}>
                {marketingTemplate?.oneLiner || 'User Selling Proposition'}
              </h2>
            </div>

            {/* Right Form - Glass Effect */}
            <div className="w-[35%] bg-white/20 backdrop-blur-md p-6 rounded-lg shadow-xl mt-3">
              <h3
                className={`font-semibold mb-4 text-lg ${
                  selectedColor === 'white' ? 'text-white' : 'text-black'
                }`}>
                Fill Details
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                {AllQuestions.map((question) => (
                  <div key={question.id}>
                    <label
                      className={`block text-sm mb-1 font-medium ${
                        selectedColor === 'white' ? 'text-white/90' : 'text-black/90'
                      }`}>
                      {formatLabel(question.name)} *
                    </label>
                    {renderInput(question.name, formData[question.name])}
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-md font-medium transition-colors mt-4 shadow-lg disabled:opacity-50 ${
                    selectedColor === 'white'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-black hover:bg-gray-800 text-white'
                  }`}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div
              className={`w-[35%] flex items-center justify-start gap-4 text-sm mt-2 mb-4 ${
                selectedColor === 'white' ? 'text-white' : 'text-black'
              }`}>
              <div className="flex items-center gap-2">
                <Phone
                  className={`w-4 h-4 ${selectedColor === 'white' ? 'text-white' : 'text-black'}`}
                />
                <span>{companyInfo?.company_number || '+91-XXXXXXXXX'}</span>
              </div>
              <div
                className={`w-px h-4 ${
                  selectedColor === 'white' ? 'bg-white/40' : 'bg-black/40'
                }`}></div>
              <div className="flex items-center gap-2">
                <Mail
                  className={`w-4 h-4 ${selectedColor === 'white' ? 'text-white' : 'text-black'}`}
                />
                <span>{companyInfo?.company_email || 'info@housingram.com'}</span>
              </div>
            </div>
          </div>

          {isPreviewMode && (
            <div className="absolute right-3 top-4 z-50">
              <Button onClick={onConfirm}>Confirm</Button>
            </div>
          )}
        </div>
      ) : (
        // Mobile View
        <div className="min-h-screen">
          <div className="w-full h-screen rounded-lg overflow-hidden">
            <div
              className="h-full relative overflow-auto flex flex-col"
              style={{
                backgroundImage: marketingTemplate?.phoneBanner_url
                  ? `url(${marketingTemplate.phoneBanner_url})`
                  : marketingTemplate?.webBanner_url
                    ? `url(${marketingTemplate.webBanner_url})`
                    : 'linear-gradient(to bottom right, #808080, #BEBEBE)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}>
              {/* Status Bar */}
              <div
                className={`p-2 text-center text-xs flex justify-between items-center px-4 ${
                  selectedColor === 'white' ? 'text-white' : 'text-black'
                }`}>
                <span>9:41</span>
                <img src={Network} alt="Network" className="h-2" />
              </div>

              {/* Header */}
              <div
                className={`p-2 flex items-center justify-end w-full ${
                  selectedColor === 'white' ? 'text-white' : 'text-black'
                }`}>
                <img src={LOGO} alt="HousinGram Logo" className="h-6" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-4 flex flex-col h-full">
                {!showMobileForm ? (
                  <>
                    <div className="flex-1">
                      <h3
                        className={`text-lg font-semibold mb-2 leading-tight ${
                          selectedColor === 'white' ? 'text-white' : 'text-black'
                        }`}>
                        {marketingTemplate?.oneLiner || 'User Selling Proposition'}
                      </h3>
                    </div>

                    <div className="">
                      <button
                        onClick={handleMobileInterest}
                        className={`w-full backdrop-blur-md hover:bg-white/25 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg ${
                          selectedColor === 'white'
                            ? 'bg-white/20 text-white'
                            : 'bg-black/20 text-black'
                        }`}>
                        I&apos;m Interested
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <h3
                        className={`text-xl font-semibold mb-2 leading-tight ${
                          selectedColor === 'white' ? 'text-white' : 'text-black'
                        }`}>
                        {marketingTemplate?.oneLiner || 'User Selling Proposition'}
                      </h3>
                    </div>

                    <div className="bg-white/20 backdrop-blur-md rounded-lg p-3 space-y-2 h-fit">
                      <h4
                        className={`font-semibold text-sm mb-2 ${
                          selectedColor === 'white' ? 'text-white' : 'text-black'
                        }`}>
                        Fill Details
                      </h4>
                      <form onSubmit={handleSubmit} className="space-y-2 h-fit">
                        {AllQuestions.map((question) => (
                          <div key={question.id}>
                            <label
                              className={`block text-xs mb-1 ${
                                selectedColor === 'white' ? 'text-white/90' : 'text-black/90'
                              }`}>
                              {formatLabel(question.name)} *
                            </label>
                            {renderInput(question.name, formData[question.name], true)}
                          </div>
                        ))}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`w-full py-2 rounded text-xs font-medium mt-2 transition-colors disabled:opacity-50 ${
                            selectedColor === 'white'
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-black hover:bg-gray-800 text-white'
                          }`}>
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </form>
                    </div>
                  </>
                )}
              </div>

              {/* Contact Info - Always at bottom */}
              <div
                className={`flex items-center justify-center gap-2 p-4 mt-auto ${
                  selectedColor === 'white' ? 'text-white' : 'text-black'
                }`}>
                <div className="flex items-center gap-1">
                  <Phone
                    className={`w-3 h-3 ${selectedColor === 'white' ? 'text-white' : 'text-black'}`}
                  />
                  <span className="text-[9px]">
                    {companyInfo?.company_number || '+91-XXXXXXXXX'}
                  </span>
                </div>
                <div
                  className={`w-px h-3 ${
                    selectedColor === 'white' ? 'bg-white/40' : 'bg-black/40'
                  }`}></div>
                <div className="flex items-center gap-1">
                  <Mail
                    className={`w-3 h-3 ${selectedColor === 'white' ? 'text-white' : 'text-black'}`}
                  />
                  <span className="text-[9px]">
                    {companyInfo?.company_email || 'info@housingram.com'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {isPreviewMode && (
            <div className="fixed right-3 top-4 z-50">
              <Button onClick={onConfirm}>Confirm</Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TemplateOne;
